import { useMemo } from "react";
import { ConnectProto } from "@racoon/api";
import { useListCounterpartiesQuery } from "~/shared/api/organization/organization.grpc";
import { useSelectedCompany } from "~/shared/store/company/hooks/useSelectedCompany";

/**
 * The selected company's counterparties as customer-picker options: `selectable`
 * keeps the active ones plus the one already picked, so editing a record whose
 * customer was deactivated still shows that customer.
 */
export const useSelectableCounterparties = (selectedOrgId: string) => {
  const { selectedCompanyId } = useSelectedCompany();
  // `currentData`, not `data`: after a company switch `data` still holds the
  // previous company's counterparties until the new request lands.
  const { currentData: data, isFetching: isLoading } = useListCounterpartiesQuery(
    { organizationId: BigInt(selectedCompanyId ?? 0) },
    { skip: !selectedCompanyId },
  );

  const organizations = useMemo(
    () => (data?.counterparties ?? []).flatMap((c) => (c.organization ? [c.organization] : [])),
    [data],
  );
  const selectable = useMemo(
    () =>
      organizations.filter(
        (org) =>
          org.status === ConnectProto.OrganizationStatus.ACTIVE || String(org.id) === selectedOrgId,
      ),
    [organizations, selectedOrgId],
  );

  return { organizations, selectable, isLoading };
};
